import { ItemKind, type SimEvent } from '../sim/types.ts';

/**
 * All sound in Moonseed Portals (brief §13): synthesized live through the Web
 * Audio API, nothing loaded from a file. That is a provenance decision as much
 * as an aesthetic one - zero licensing surface, near-zero payload, and no
 * asset pipeline to keep in sync with the sim.
 *
 * A module-level singleton, not a per-room object: the room-scoped
 * `Simulation`/`Renderer` pair is torn down and rebuilt on every room change
 * (`useGame`'s effect keys off `room`), but recreating an `AudioContext` on
 * every door walked through would be wasteful and would restart the ambient
 * pad every time. This lives for the whole session instead, exactly like the
 * `localStorage`-backed mute preference it owns.
 */

const STORAGE_KEY = 'moonseed:audio';
/** Below this, the low-moonlight alarm loop starts - matches the HUD's own
 *  `.is-low` visual pulse threshold (`Hud.tsx`), so the audio and visual
 *  warnings always agree (brief §11/§13: neither carries the cue alone). */
const LOW_MOONLIGHT_THRESHOLD = 15;
const LOW_MOONLIGHT_INTERVAL_MS = 1100;
/** How far ducking (pause) pulls the music bus down, and how fast it and any
 *  mute toggle ramps - soft enough that "ducks" reads as ducking, not
 *  muting, and no gain change ever clicks. */
const DUCK_LEVEL = 0.32;
const RAMP_SECONDS = 0.12;
const SFX_BASE_GAIN = 0.5;
/** The ambient pad's base level, 60% below its original 0.16 - direct
 *  request after the first pass shipped it too loud relative to SFX. */
const MUSIC_BASE_GAIN = 0.16 * 0.4;

interface AudioPrefs {
  sfxMuted: boolean;
  musicMuted: boolean;
}

/** Mirrors `progress.ts`'s own localStorage discipline: every call here is a
 *  convenience, never something gameplay depends on to run. Reads the
 *  original single `muted` field as a seed for both, if present, so a
 *  player who muted before this split doesn't silently get unmuted. */
function loadPrefs(): AudioPrefs {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { sfxMuted: false, musicMuted: false };
    const parsed = JSON.parse(raw) as { sfxMuted?: unknown; musicMuted?: unknown; muted?: unknown } | null;
    const legacy = typeof parsed?.muted === 'boolean' ? parsed.muted : false;
    return {
      sfxMuted: typeof parsed?.sfxMuted === 'boolean' ? parsed.sfxMuted : legacy,
      musicMuted: typeof parsed?.musicMuted === 'boolean' ? parsed.musicMuted : legacy,
    };
  } catch {
    return { sfxMuted: false, musicMuted: false };
  }
}

function savePrefs(prefs: AudioPrefs): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Preference just won't persist this session.
  }
}

/** Soft/magical palette throughout: sine and triangle tones with gentle
 *  attack/release envelopes, never a bare square or sawtooth wave. */
type ToneShape = 'sine' | 'triangle';

class AudioEngine {
  private ctx: AudioContext | null = null;
  /** SFX has no duck state of its own - nothing on this bus ever plays while
   *  paused (no sim events are emitted then), so only its mute matters. */
  private sfxGain: GainNode | null = null;
  /** Music is the one bus ducking actually affects - it is the only thing
   *  still audible while paused. */
  private musicGain: GainNode | null = null;
  private sfxMuted: boolean;
  private musicMuted: boolean;
  private ducked = false;
  private unlocked = false;
  private lowMoonlightTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    const prefs = loadPrefs();
    this.sfxMuted = prefs.sfxMuted;
    this.musicMuted = prefs.musicMuted;
    if (typeof window === 'undefined') return;
    const unlock = (): void => {
      if (this.unlocked) return;
      this.unlocked = true;
      const ctx = this.ensureContext();
      void ctx.resume();
      this.startMusic();
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('pointerdown', unlock);
    };
    // Browser autoplay policy: the context must start suspended until a real
    // user gesture. Any key or pointer press anywhere unlocks it once - the
    // player doesn't have to find a specific "enable audio" button first.
    window.addEventListener('keydown', unlock);
    window.addEventListener('pointerdown', unlock);
  }

  private ensureContext(): AudioContext {
    if (this.ctx) return this.ctx;
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    const ctx = new AC();
    this.ctx = ctx;
    this.sfxGain = ctx.createGain();
    this.sfxGain.gain.value = this.sfxMuted ? 0 : SFX_BASE_GAIN;
    this.sfxGain.connect(ctx.destination);
    this.musicGain = ctx.createGain();
    this.musicGain.gain.value = this.musicMuted ? 0 : MUSIC_BASE_GAIN;
    this.musicGain.connect(ctx.destination);
    return ctx;
  }

  isSfxMuted(): boolean {
    return this.sfxMuted;
  }

  isMusicMuted(): boolean {
    return this.musicMuted;
  }

  toggleSfxMute(): boolean {
    this.sfxMuted = !this.sfxMuted;
    savePrefs({ sfxMuted: this.sfxMuted, musicMuted: this.musicMuted });
    if (this.ctx && this.sfxGain) {
      this.sfxGain.gain.setTargetAtTime(this.sfxMuted ? 0 : SFX_BASE_GAIN, this.ctx.currentTime, RAMP_SECONDS);
    }
    return this.sfxMuted;
  }

  toggleMusicMute(): boolean {
    this.musicMuted = !this.musicMuted;
    savePrefs({ sfxMuted: this.sfxMuted, musicMuted: this.musicMuted });
    if (this.ctx && this.musicGain) {
      const level = this.musicMuted ? 0 : this.ducked ? MUSIC_BASE_GAIN * DUCK_LEVEL : MUSIC_BASE_GAIN;
      this.musicGain.gain.setTargetAtTime(level, this.ctx.currentTime, RAMP_SECONDS);
    }
    return this.musicMuted;
  }

  /** Called every frame with the current pause state. Music keeps playing,
   *  quieter, rather than cutting - "ducks on pause" (brief §13), not mutes.
   *  SFX is untouched: nothing on that bus plays while paused anyway, since
   *  no sim events are emitted then. */
  setDucked(ducked: boolean): void {
    if (this.ducked === ducked) return;
    this.ducked = ducked;
    if (!this.ctx || !this.musicGain || this.musicMuted) return;
    const level = ducked ? MUSIC_BASE_GAIN * DUCK_LEVEL : MUSIC_BASE_GAIN;
    this.musicGain.gain.setTargetAtTime(level, this.ctx.currentTime, RAMP_SECONDS);
  }

  /** Called every frame with whether the alarm should currently be playing
   *  (moonlight below threshold, room actually live). Idempotent. */
  setLowMoonlight(active: boolean): void {
    if (active === (this.lowMoonlightTimer !== null)) return;
    if (!active) {
      if (this.lowMoonlightTimer !== null) clearInterval(this.lowMoonlightTimer);
      this.lowMoonlightTimer = null;
      return;
    }
    this.alarmBeep();
    this.lowMoonlightTimer = setInterval(() => this.alarmBeep(), LOW_MOONLIGHT_INTERVAL_MS);
  }

  /** One tick's worth of sim events, mapped to cues. Events emitted before
   *  the context unlocks are simply silent - never queued, never replayed -
   *  matching the autoplay policy rather than fighting it. */
  handleEvents(events: readonly SimEvent[]): void {
    if (!this.unlocked) return;
    for (const e of events) this.playForEvent(e);
  }

  private playForEvent(e: SimEvent): void {
    switch (e.type) {
      case 'cast:create':
        this.tone(520, 0.09, { shape: 'triangle', gain: 0.5 });
        break;
      case 'cast:destroy':
        this.noiseBurst(0.16, { filterFreq: 900, gain: 0.4 });
        break;
      case 'cast:refused':
      case 'cast:stone':
        this.tone(160, 0.08, { shape: 'sine', gain: 0.3 });
        break;
      case 'block:hit':
        this.tone(300, 0.05, { shape: 'triangle', gain: 0.25 });
        break;
      case 'player:land':
        this.noiseBurst(0.07, { filterFreq: 500, gain: 0.22 });
        break;
      case 'player:jump':
        this.tone(440, 0.07, { shape: 'sine', gain: 0.22 });
        break;
      case 'chimera:banished':
        this.pop();
        break;
      case 'ranged:fired':
        this.whoosh();
        break;
      case 'ranged:empty':
      case 'door:locked':
        this.tone(180, 0.1, { shape: 'sine', gain: 0.28 });
        break;
      case 'door:entered':
        this.chime([660, 880], 0.12);
        break;
      case 'item:taken':
        if (e.kind === ItemKind.Moonseed) this.moonseedMotif();
        else this.tone(880, 0.08, { shape: 'sine', gain: 0.3 });
        break;
      case 'portal:open':
        this.chime([440, 660, 880], 0.16);
        break;
      case 'room:cleared':
        this.chime([523, 659, 784, 1046], 0.18);
        break;
      case 'room:failed':
        this.chime([392, 330, 261], 0.22);
        break;
      default:
        break;
    }
  }

  // ---------- low-level synthesis ----------

  private tone(
    freq: number,
    duration: number,
    opts: { shape: ToneShape; gain: number; detune?: number } = { shape: 'sine', gain: 0.3 },
  ): void {
    const ctx = this.ctx;
    const bus = this.sfxGain;
    if (!ctx || !bus) return;
    const osc = ctx.createOscillator();
    osc.type = opts.shape;
    osc.frequency.value = freq;
    if (opts.detune) osc.detune.value = opts.detune;
    const gain = ctx.createGain();
    const now = ctx.currentTime;
    const attack = Math.min(0.02, duration * 0.3);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(opts.gain, now + attack);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.connect(gain);
    gain.connect(bus);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  /** A short ascending three-note phrase, not a plain blip - the brief's
   *  "single motif on the Moonseed pickup" (§13), distinct from every other
   *  item's plain tone. */
  private moonseedMotif(): void {
    const notes = [659, 880, 1108];
    notes.forEach((freq, i) => {
      const ctx = this.ctx;
      if (!ctx) return;
      const delay = i * 0.09;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      const bus = this.sfxGain;
      if (!bus) return;
      const start = ctx.currentTime + delay;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.35, start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.3);
      osc.connect(gain);
      gain.connect(bus);
      osc.start(start);
      osc.stop(start + 0.32);
    });
  }

  /** A short chord/arpeggio for multi-note cues (doors, portal, room
   *  cleared/failed) - same building block, different note lists. */
  private chime(freqs: readonly number[], duration: number): void {
    const ctx = this.ctx;
    const bus = this.sfxGain;
    if (!ctx || !bus) return;
    const now = ctx.currentTime;
    for (const freq of freqs) {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.22, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
      osc.connect(gain);
      gain.connect(bus);
      osc.start(now);
      osc.stop(now + duration + 0.02);
    }
  }

  private noiseBuffer(duration: number): AudioBuffer {
    const ctx = this.ensureContext();
    const length = Math.max(1, Math.floor(ctx.sampleRate * duration));
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    // Deterministic-looking but not gameplay-facing: SFX never feeds back
    // into the simulation, so `Math.random()` here does not violate the
    // sim's own no-randomness rule (brief §5) - it only ever touches sound.
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
  }

  /** A soft filtered noise burst - crumbling a block, landing underfoot.
   *  Low-passed so it reads as a dull thud/rustle, never a harsh hiss. */
  private noiseBurst(duration: number, opts: { filterFreq: number; gain: number }): void {
    const ctx = this.ctx;
    const bus = this.sfxGain;
    if (!ctx || !bus) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer(duration);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = opts.filterFreq;
    const gain = ctx.createGain();
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(opts.gain, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(bus);
    src.start(now);
    src.stop(now + duration + 0.02);
  }

  /** A killed Chimera: a quick downward-pitched pop. */
  private pop(): void {
    const ctx = this.ctx;
    const bus = this.sfxGain;
    if (!ctx || !bus) return;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    const now = ctx.currentTime;
    osc.frequency.setValueAtTime(700, now);
    osc.frequency.exponentialRampToValueAtTime(180, now + 0.15);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.32, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.16);
    osc.connect(gain);
    gain.connect(bus);
    osc.start(now);
    osc.stop(now + 0.18);
  }

  /** The fireball firing: a quick upward-pitched sweep under filtered noise. */
  private whoosh(): void {
    const ctx = this.ctx;
    const bus = this.sfxGain;
    if (!ctx || !bus) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(220, now);
    osc.frequency.exponentialRampToValueAtTime(660, now + 0.12);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.13);
    osc.connect(gain);
    gain.connect(bus);
    osc.start(now);
    osc.stop(now + 0.14);
  }

  /** One pulse of the low-moonlight alarm - two soft descending notes,
   *  paired with the HUD's own visual pulse (`Hud.tsx`'s `.is-low` class). */
  private alarmBeep(): void {
    this.tone(660, 0.12, { shape: 'sine', gain: 0.24 });
    const ctx = this.ctx;
    if (ctx) {
      const bus = this.sfxGain;
      if (bus) {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = 520;
        const gain = ctx.createGain();
        const start = ctx.currentTime + 0.13;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.22, start + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.16);
        osc.connect(gain);
        gain.connect(bus);
        osc.start(start);
        osc.stop(start + 0.18);
      }
    }
  }

  /**
   * The single ambient loop (brief §13: "one or two... sparse pads"): two
   * detuned sine oscillators through a lowpass filter, with a slow LFO
   * breathing the filter cutoff so it never sits perfectly static. Starts
   * once, on unlock, and runs for the rest of the session - it is not
   * recreated per room, the same reasoning as the engine itself being a
   * singleton.
   */
  private startMusic(): void {
    const ctx = this.ctx;
    const bus = this.musicGain;
    if (!ctx || !bus) return;
    const now = ctx.currentTime;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 900;
    filter.connect(bus);

    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.05;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 260;
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start(now);

    // A sparse, slowly-detuned pad: two low sines a soft interval apart.
    for (const [freq, detune] of [
      [130.81, -4],
      [196.0, 5],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.detune.value = detune;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.6, now + 3);
      osc.connect(gain);
      gain.connect(filter);
      osc.start(now);
    }
  }
}

export const audio = new AudioEngine();
export { LOW_MOONLIGHT_THRESHOLD };
